import React from 'react';
import { Router, createBrowserHistory } from 'react-resource-router';
import { Root, Main } from '@atlaskit/navigation-system';
import AppProvider from '@atlaskit/app-provider';
import { routes } from './routes';
import { NavigationBar } from './components/NavigationBar';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { IntlProvider } from 'react-intl';
import { UserProvider } from '../auth/user-provider';
import { FlagsProvider } from '@atlaskit/flag';
import { PanelProvider } from './contexts/PanelContext';
import { LabsProvider } from './pages/labs/home/context/LabsContext';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { LeftSidebarProvider } from './contexts/LeftSidebarContext';
import { AsideProvider } from './contexts/AsideContext';
import { RightSidePanel, RightAsidePanel, MainWithLeftSidebar } from './components/AppNavigation';
import { useAtomValue } from 'jotai';
import { IsFocusModeAtom } from './pages/graph-explorer/home/ObjectsVisualizer/atoms/focusAtoms';
import { AnalyticsProvider } from '../common/analytics/AnalyticsProvider';
import { initAnalyticsClient } from '../common/analytics/analytics-client';

const analyticsClient = initAnalyticsClient();

const styles = cssMap({
	rootContainer: {
		height: '100vh',
	},
});

/**
 * Conditionally renders the top NavigationBar.
 * Hidden during Graph Explorer focus mode so the full-screen overlay
 * doesn't need to fight parent stacking contexts.
 */
const ConditionalNavigationBar = (): JSX.Element | null => {
	const isFocusMode = useAtomValue(IsFocusModeAtom);
	if (isFocusMode) {
		return null;
	}
	return <NavigationBar />;
};

export const App = (): JSX.Element => {
	return (
		<RelayEnvironmentProvider>
			<IntlProvider locale="en">
				<AnalyticsProvider analyticsClient={analyticsClient}>
					<AppProvider>
						<FlagsProvider>
							<UserProvider>
								<Router history={createBrowserHistory()} routes={routes}>
									<Box xcss={styles.rootContainer}>
										<Root>
											<LabsProvider>
												<PanelProvider>
													<LeftSidebarProvider>
														<AsideProvider>
															{/* TopNav — hidden during Graph Explorer focus mode */}
															<ConditionalNavigationBar />
															{/* Main */}
															<Main>
																<MainWithLeftSidebar />
															</Main>
															{/* Aside */}
															<RightAsidePanel />
															{/* Panel */}
															<RightSidePanel />
														</AsideProvider>
													</LeftSidebarProvider>
												</PanelProvider>
											</LabsProvider>
										</Root>
									</Box>
								</Router>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</AnalyticsProvider>
			</IntlProvider>
		</RelayEnvironmentProvider>
	);
};
